import React, { forwardRef, useEffect, useImperativeHandle, useRef } from "react";

declare global {
  interface Window {
    turnstile?: {
      render: (container: HTMLElement, options: Record<string, unknown>) => string;
      reset: (widgetId: string) => void;
      remove: (widgetId: string) => void;
    };
  }
}

export type TurnstileWidgetHandle = {
  reset: () => void;
};

type TurnstileWidgetProps = {
  action: string;
  onToken: (token: string) => void;
  // "always" (default) shows the normal checkbox UI - use for a screen's
  // primary/sole verification. "interaction-only" renders no visible box and
  // solves silently in the background, only surfacing a challenge if
  // Cloudflare can't auto-verify - use for a secondary gate stacked next to
  // an already-visible widget or an ordinary button, so it doesn't look like
  // a duplicate captcha.
  appearance?: "always" | "execute" | "interaction-only";
};

// Renders the Cloudflare Turnstile widget explicitly (rather than relying on
// the implicit data-sitekey div) so the widget ID can be retained and reset
// between submit attempts on this SPA, since tokens are single-use.
const TurnstileWidget = forwardRef<TurnstileWidgetHandle, TurnstileWidgetProps>(
  ({ action, onToken, appearance = "always" }, ref) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const widgetIdRef = useRef<string | null>(null);

    useEffect(() => {
      let cancelled = false;
      let pollId: ReturnType<typeof setInterval> | undefined;

      const renderWidget = () => {
        if (cancelled || !containerRef.current || !window.turnstile) return;
        widgetIdRef.current = window.turnstile.render(containerRef.current, {
          sitekey: process.env.REACT_APP_TURNSTILE_SITE_KEY,
          action,
          appearance,
          callback: (token: string) => onToken(token),
          "expired-callback": () => onToken(""),
          "error-callback": () => onToken(""),
        });
      };

      if (window.turnstile) {
        renderWidget();
      } else {
        pollId = setInterval(() => {
          if (window.turnstile) {
            clearInterval(pollId);
            renderWidget();
          }
        }, 100);
      }

      return () => {
        cancelled = true;
        if (pollId) clearInterval(pollId);
        if (widgetIdRef.current && window.turnstile) {
          window.turnstile.remove(widgetIdRef.current);
          widgetIdRef.current = null;
        }
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [action]);

    useImperativeHandle(ref, () => ({
      reset: () => {
        if (widgetIdRef.current && window.turnstile) {
          window.turnstile.reset(widgetIdRef.current);
        }
        onToken("");
      },
    }));

    return <div ref={containerRef} />;
  }
);

export default TurnstileWidget;
